/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import { useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { isEmpty } from "lodash"
import { AppState } from "store/store.type"
import { actionCreator } from "store/store"
import { useEnablePartialMenu } from "hooks/useEnablePartialMenu"
import { isItemWithoutPhotoMode, isMenuFilterActive } from "utils/menu_helper"
import { useMenuOverviewDepartments } from "./useMenuOverviewDepartments"
import { useMenuOverviewSubDepartments } from "./useMenuOverviewSubDepartments"

/**
 * Keeps overview department/sub-department selection consistent with the
 * active filter, re-selecting from the filtered lists when the current
 * selection is filtered out.
 *
 * Partial menu ON: runs for any active filter (photo or selling time). V3
 * hierarchy selection leaves overview.subDepartments empty; displayv2 must
 * re-populate Redux via selectDepartment even when the department ID is
 * unchanged. When no filters are active, V3 components handle their own
 * auto-selection.
 *
 * Partial menu OFF: runs for the photo filter only. Redux is already
 * populated, but handleSelectOverviewDepartment auto-picks the first
 * sub-department ignoring the filter — an all-photos first sub-department
 * zeroes the filtered items and dead-ends on the "perfect photo coverage"
 * empty state. Selling time keeps its own legacy flow
 * (handleSelectOverviewSellingTime).
 *
 * Skipped while an item deeplink (overview.itemID) is active: re-selecting
 * a sub-department resets itemID and would clobber a search selection.
 */
export const useMenuAutoSelection = () => {
  const dispatch = useDispatch()
  const enablePartialMenu = useEnablePartialMenu()

  const {
    overview: {
      department: selectedDepartment,
      subDepartment: selectedSubDepartment,
      sellingTime,
      itemID,
    },
    isMartDepartmentStructure,
    itemFilter,
  } = useSelector((state: AppState) => state.menu)

  // Check if filters are active - only run auto-selection when using legacy components
  const isFilterActive = isMenuFilterActive(itemFilter, sellingTime)

  const shouldAutoSelect =
    (enablePartialMenu ? isFilterActive : isItemWithoutPhotoMode(itemFilter)) &&
    isEmpty(itemID)

  // Get filtered data from hooks
  const {
    filteredDepartments,
    isLoading: isDepartmentsLoading,
  } = useMenuOverviewDepartments()
  const {
    filteredSubDepartments,
    isLoading: isSubDepartmentsLoading,
  } = useMenuOverviewSubDepartments()

  const selectedDepartmentID = selectedDepartment?.itemClassID

  useEffect(() => {
    if (
      shouldAutoSelect &&
      isMartDepartmentStructure &&
      !isDepartmentsLoading &&
      filteredDepartments.length > 0
    ) {
      const matchedDepartment = selectedDepartment
        ? filteredDepartments.find(
            dept => dept.itemClassID === selectedDepartment.itemClassID,
          )
        : null

      dispatch(
        actionCreator.menu.selectDepartment(
          matchedDepartment ?? filteredDepartments[0],
        ),
      )
    }
    // selectedDepartment is read for ID matching only; omit from deps to avoid
    // re-sync loops — filteredDepartments updates on filter/data changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    shouldAutoSelect,
    isMartDepartmentStructure,
    isDepartmentsLoading,
    filteredDepartments,
    dispatch,
  ])

  useEffect(() => {
    if (
      shouldAutoSelect &&
      isMartDepartmentStructure &&
      selectedDepartmentID &&
      !isSubDepartmentsLoading &&
      filteredSubDepartments.length > 0
    ) {
      const matchedSubDepartment = selectedSubDepartment
        ? filteredSubDepartments.find(
            sd => sd.itemClassID === selectedSubDepartment.itemClassID,
          )
        : null

      dispatch(
        actionCreator.menu.selectSubDepartment(
          matchedSubDepartment ?? filteredSubDepartments[0],
        ),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    shouldAutoSelect,
    isMartDepartmentStructure,
    isSubDepartmentsLoading,
    filteredSubDepartments,
    selectedDepartmentID,
    dispatch,
  ])
}
